// Iteration 75 — Runtime audio polish: 350ms fade-out on dialogue clips.
//
// The default <audio> element ends dialogue with an abrupt cut, which slightly
// breaks the illusion that Serine is beside the listener. This helper attaches
// a smooth volume ramp over the last `fadeMs` of playback so every spoken clip
// exits with a soft trailing breath instead of a hard silence edge.
//
// Behaviour:
//   • Monitors currentTime via rAF and kicks off a linear volume ramp when
//     playback enters the last `fadeMs` window.
//   • Does NOT touch volume outside the fade-out ramp — the opening audio's
//     own fade-in ramp is preserved as-is.
//   • On pause / ended / error, cancels the loop; on resume (play), restarts
//     monitoring so pause-then-resume still fades cleanly.
//   • Idempotent per element — re-attaching is a no-op.

export function attachDialogueFadeOut(audioEl, fadeMs = 350) {
  if (!audioEl || audioEl.__continua_fade_attached) return;
  audioEl.__continua_fade_attached = true;

  let rafId = null;
  let fadeStartMs = null;
  let fadeStartVol = null;

  const cancelLoop = () => {
    if (rafId != null) {
      try { cancelAnimationFrame(rafId); } catch (e) { /* ignore */ }
      rafId = null;
    }
    fadeStartMs = null;
    fadeStartVol = null;
  };

  const now = () => (typeof performance !== "undefined" ? performance.now() : Date.now());

  const tick = () => {
    rafId = null;
    if (!audioEl || audioEl.paused || audioEl.ended) {
      cancelLoop();
      return;
    }
    const dur = audioEl.duration;
    if (!isFinite(dur) || dur <= 0) {
      rafId = requestAnimationFrame(tick);
      return;
    }
    const fadeStartSec = Math.max(0, dur - fadeMs / 1000);
    if (audioEl.currentTime < fadeStartSec) {
      rafId = requestAnimationFrame(tick);
      return;
    }
    // Enter (or continue) the fade window.
    if (fadeStartMs == null) {
      fadeStartMs = now();
      fadeStartVol = audioEl.volume;
    }
    const p = Math.min(1, (now() - fadeStartMs) / fadeMs);
    try { audioEl.volume = Math.max(0, fadeStartVol * (1 - p)); } catch (e) { /* ignore */ }
    if (p < 1) {
      rafId = requestAnimationFrame(tick);
    } else {
      cancelLoop();
    }
  };

  const onPlay = () => {
    // Reset fade tracking so pause-then-resume gets a fresh fade window.
    fadeStartMs = null;
    fadeStartVol = null;
    if (rafId == null) rafId = requestAnimationFrame(tick);
  };
  const onPauseOrEnd = () => cancelLoop();

  audioEl.addEventListener("play", onPlay);
  audioEl.addEventListener("ended", onPauseOrEnd);
  audioEl.addEventListener("pause", onPauseOrEnd);
  audioEl.addEventListener("error", onPauseOrEnd);

  // If already playing (autoplay-in-progress), kick immediately.
  if (!audioEl.paused && !audioEl.ended) {
    onPlay();
  }
}

// Iter 90 — full fade treatment for every TTS clip: short fade-in on play
// (default 120ms, spec 50-150ms) + gentle fade-out (default 250ms, spec
// 100-250ms). The element's volume at attach time is the mix target.
export function attachDialogueFades(audioEl, { fadeInMs = 200, fadeOutMs = 300 } = {}) {
  if (!audioEl || audioEl.__continua_fadein_attached) return;
  audioEl.__continua_fadein_attached = true;

  const baseVol = audioEl.volume;
  let rafId = null;

  const now = () => (typeof performance !== "undefined" ? performance.now() : Date.now());

  const onPlay = () => {
    // Only fade in from the top of the clip — resuming mid-clip keeps volume.
    if (audioEl.currentTime > 0.35) return;
    const t0 = now();
    try { audioEl.volume = 0; } catch (e) { /* ignore */ }
    const step = () => {
      rafId = null;
      if (!audioEl || audioEl.paused || audioEl.ended) return;
      const p = Math.min(1, (now() - t0) / fadeInMs);
      try { audioEl.volume = baseVol * p; } catch (e) { /* ignore */ }
      if (p < 1) rafId = requestAnimationFrame(step);
    };
    if (rafId == null) rafId = requestAnimationFrame(step);
  };

  audioEl.addEventListener("play", onPlay);
  if (!audioEl.paused && !audioEl.ended) onPlay();

  // Iter 103 — NO fade-out during speech: the user could hear clips being cut.
  // Clips now end on their own natural trailing silence; only the gentle
  // fade-in remains so boundaries are felt as breaths, not edits.
  if (fadeOutMs > 0) attachDialogueFadeOut(audioEl, fadeOutMs);
}
